import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import VerificationIcon from "../../VerificationIcon";
import {
  getDefaultImage,
  calcRemainingDays,
  genStatusText,
} from "../../../utils/GeneralUtils";
import { PageVisibility } from "../../../utils/PageVisibility";
import styles from "./SettingsItem.module.scss";
import menuStyles from "./SettingsItemMenu.module.scss";
import SonicLink from "../../routing/SonicLink";

type Props = {
  page_type: string;
  id?: string;
  name?: string;
  img_src?: string | undefined;
  type?: { color?: string; name?: string };
  applicants?: Array<string>;
  start_date?: number;
  end_date?: number;
  status?: string;
  handleMenu: () => void;
  handlePublish?: (id?: string) => void;
  handlePrivate?: (id?: string) => void;
  edit_link?: string;
  member_link?: string;
  detail_link: string;
  isMenuOpen: boolean;
  isOwner: boolean;
  borderColorStyle?: string;
  typeTagStyle?: string;
  verified?: boolean;
};

const SettingsItem: React.FC<Props> = (props) => {
  const {
    page_type,
    id,
    name,
    img_src,
    type,
    applicants,
    start_date,
    end_date,
    status,
    handleMenu,
    handlePublish,
    handlePrivate,
    edit_link,
    member_link,
    detail_link,
    isMenuOpen,
    isOwner,
    borderColorStyle,
    typeTagStyle,
    verified,
  } = props;

  const remainingDay = end_date ? calcRemainingDays(end_date) : undefined;

  let status_class = "";
  switch (status) {
    case PageVisibility.AwaitingInspection:
      status_class = styles["pending"];
      break;
    case PageVisibility.PubliclyVisible:
      status_class = styles["open"];
      if (remainingDay !== undefined && remainingDay < 0) {
        status_class = "";
      }
      break;
    default:
      break;
  }

  const genRemainingDayText = (page_type: string, remainingDay?: number) => {
    switch (page_type) {
      case "プロジェクト":
      case "イベント":
        if (remainingDay === undefined) {
          return "終了日未定";
        } else if (remainingDay >= 0) {
          return `残り ${remainingDay}日`;
        } else {
          return "期間終了";
        }
      default:
        return "";
    }
  };

  return (
    <article className={styles["c-settingsItem"]}>
      <div className={styles["c-settingsItem__inner"]}>
        <div
          className={
            page_type === "チーム"
              ? `${styles["c-settingsItem__imgBox"]} ${styles["team"]}`
              : `${styles["c-settingsItem__imgBox"]}`
          }
          style={{ borderRight: `4px solid ${borderColorStyle}` }}
        >
          <img src={img_src ? img_src : getDefaultImage()} />
        </div>
        <div className={styles["c-settingsItem__info"]}>
          {isOwner === false && page_type === "チーム" ? (
            <div className={styles["c-settingsItem__info-noEdit"]}>
              <FontAwesomeIcon icon={["fal", "exclamation-circle"]} />
              <span>
                ご利用中のアカウントでは、
                <br />
                編集権限がありません
              </span>
            </div>
          ) : null}
          <div
            className={`${styles["c-settingsItem__info-textBox-status"]} ${status_class}`}
          >
            {genStatusText(status, start_date, end_date)}
          </div>
          <div className={styles["c-settingsItem__info-textBox"]}>
            <div className={styles["c-settingsItem__info-textBox-title"]}>
              {name}
            </div>
            {page_type === "プロフィール" && verified ? (
              <VerificationIcon />
            ) : null}
          </div>

          {type?.name ? (
            <div className={styles["c-settingsItem__info-typeBox"]}>
              <span
                className={styles["c-settingsItem__info-typeBox-text"]}
                style={
                  typeTagStyle
                    ? {
                        border: `1px solid ${typeTagStyle}`,
                        padding: "2px 4px",
                        borderRadius: "2px",
                        color: `${typeTagStyle}`,
                      }
                    : undefined
                }
              >
                {type?.name ? type.name : "カテゴリなし"}
              </span>
            </div>
          ) : null}
          <div className={styles["c-settingsItem__info-projectBox"]}>
            {applicants && applicants.length > 0 ? (
              <span>
                応募者{" "}
                {applicants.length > 3 || isOwner
                  ? `${applicants.length}名`
                  : "募集中"}{" "}
                &nbsp;
              </span>
            ) : null}
            <span>{genRemainingDayText(page_type, remainingDay)}</span>
          </div>
        </div>
      </div>
      <button
        className={styles["c-settingsItem__menuBtn"]}
        onClick={handleMenu}
      >
        <FontAwesomeIcon icon={["fal", "ellipsis-h"]} />
      </button>
      <div
        className={
          isMenuOpen
            ? `${menuStyles["f-settingItemMenu"]} ${menuStyles["open"]}`
            : menuStyles["f-settingItemMenu"]
        }
        onClick={handleMenu}
      >
        <div className={menuStyles["f-settingItemMenu__inner"]}>
          <div className={menuStyles["f-settingItemMenu__top"]}>
            <div>{name}</div>
            <FontAwesomeIcon
              icon={["fal", "times-circle"]}
              onClick={handleMenu}
            />
          </div>
          <ul className={menuStyles["f-settingItemMenu__list"]}>
            {isOwner !== false && edit_link ? (
              <li>
                <SonicLink
                  to={edit_link}
                  className={menuStyles["f-settingItemMenu__list__link"]}
                >
                  <FontAwesomeIcon icon={["fal", "edit"]} />
                  {page_type}編集
                </SonicLink>
              </li>
            ) : null}
            {member_link ? (
              <li>
                <SonicLink
                  to={member_link}
                  className={menuStyles["f-settingItemMenu__list__link"]}
                >
                  <FontAwesomeIcon icon={["fal", "users-class"]} />
                  チームメンバー一覧
                </SonicLink>
              </li>
            ) : null}
            {status === PageVisibility.PubliclyVisible ? (
              <li>
                <SonicLink
                  to={detail_link}
                  className={menuStyles["f-settingItemMenu__list__link"]}
                >
                  <FontAwesomeIcon icon={["fal", "info-circle"]} />
                  {page_type}詳細
                </SonicLink>
              </li>
            ) : null}
            {isOwner !== false &&
            status == PageVisibility.NotPubliclyVisible &&
            handlePublish ? (
              <li
                onClick={() => handlePublish(id)}
                className={menuStyles["f-settingItemMenu__list__private"]}
              >
                <FontAwesomeIcon icon={["fal", "lock-alt"]} />
                {page_type}公開申請
              </li>
            ) : null}
            {isOwner !== false &&
            status !== PageVisibility.NotPubliclyVisible &&
            handlePrivate ? (
              <li
                onClick={() => handlePrivate(id)}
                className={menuStyles["f-settingItemMenu__list__private"]}
              >
                <FontAwesomeIcon icon={["fal", "lock-alt"]} />
                {page_type}非公開
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </article>
  );
};

export default SettingsItem;
